import { Controller, useFieldArray, useFormContext } from 'react-hook-form'
import { Plus, Trash2 } from 'lucide-react'

import type { SettingField } from '@/components/retail/cms/site-settings-model'
import {
    SITE_ICON_KEYS,
    emptyValue,
} from '@/components/retail/cms/site-settings-model'
import { TextField } from '@/components/retail/form-bits'
import { HomeImageInput } from '@/components/retail/home-image-field'
import { HomeLinkField } from '@/components/retail/home-link-field'
import { Button } from '@/components/ui/button'
import { Field, FieldError } from '@/components/ui/field'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { m } from '@/lib/paraglide/messages'

// Render form cài đặt website từ bảng mô tả trường (site-settings-model).
// `prefix` là đường dẫn trong form ('' ở gốc, vd "badges.0" trong danh sách).

const at = (prefix: string, k: string) => (prefix ? `${prefix}.${k}` : k)

export function SettingFields({
    fields,
    prefix = '',
}: {
    fields: SettingField[]
    prefix?: string
}) {
    return (
        <div className="grid gap-4">
            {fields.map((f) => (
                <SettingFieldView key={f.k} field={f} name={at(prefix, f.k)} />
            ))}
        </div>
    )
}

function SettingFieldView({
    field: f,
    name,
}: {
    field: SettingField
    name: string
}) {
    switch (f.t) {
        case 'line':
            return <TextField name={name} label={f.label()} hint={f.hint?.()} />
        case 'text':
            return <TextField name={name} label={f.label()} rows={3} />
        case 'image':
            return <HomeImageInput name={name} label={f.label()} />
        case 'link':
            return (
                <HomeLinkField
                    name={name}
                    label={f.label()}
                    optional={!f.required}
                />
            )
        case 'icon':
            return <IconField name={name} label={f.label()} />
        case 'lines':
            return <LinesField name={name} label={f.label()} max={f.max} />
        case 'list':
            return <ListField name={name} field={f} />
        case 'group':
            return (
                <fieldset className="space-y-3 rounded-md border p-3">
                    <legend className="px-1 text-sm font-medium">
                        {f.label()}
                    </legend>
                    <SettingFields fields={f.fields} prefix={name} />
                </fieldset>
            )
    }
}

function IconField({ name, label }: { name: string; label: string }) {
    const { control } = useFormContext()
    return (
        <Field name={name} label={label}>
            <Controller
                control={control}
                name={name}
                render={({ field }) => (
                    <Select
                        value={(field.value as string) || undefined}
                        onValueChange={field.onChange}
                    >
                        <SelectTrigger id={name} className="w-full">
                            <SelectValue
                                placeholder={m.siteSettings_icon_pick()}
                            />
                        </SelectTrigger>
                        <SelectContent>
                            {SITE_ICON_KEYS.map((k) => (
                                <SelectItem key={k} value={k}>
                                    {iconLabel(k)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}
            />
        </Field>
    )
}

export function iconLabel(k: string): string {
    switch (k) {
        case 'leaf':
            return m.siteSettings_icon_leaf()
        case 'leaf_simple':
            return m.siteSettings_icon_leaf_simple()
        case 'truck':
            return m.siteSettings_icon_truck()
        case 'qr_pay':
            return m.siteSettings_icon_qr_pay()
        case 'shield_check':
            return m.siteSettings_icon_shield_check()
        case 'return':
            return m.siteSettings_icon_return()
        case 'gift':
            return m.siteSettings_icon_gift()
        case 'phone':
            return m.siteSettings_icon_phone()
        case 'star':
            return m.siteSettings_icon_star()
        case 'lock':
            return m.siteSettings_icon_lock()
        case 'info':
            return m.siteSettings_icon_info()
        default:
            return m.siteSettings_icon_clock()
    }
}

function AddButton({
    onClick,
    disabled,
    label,
}: {
    onClick: () => void
    disabled: boolean
    label: string
}) {
    return (
        <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onClick}
            disabled={disabled}
        >
            <Plus className="size-4" />
            {label}
        </Button>
    )
}

function RemoveButton({
    onClick,
    label,
}: {
    onClick: () => void
    label: string
}) {
    return (
        <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onClick}
            aria-label={label}
        >
            <Trash2 className="size-4" />
        </Button>
    )
}

function LinesField({
    name,
    label,
    max,
}: {
    name: string
    label: string
    max: number
}) {
    const { control } = useFormContext()
    const { fields, append, remove } = useFieldArray({ control, name })
    return (
        <fieldset className="space-y-2 rounded-md border p-3">
            <legend className="px-1 text-sm font-medium">{label}</legend>
            {fields.map((row, i) => (
                <div key={row.id} className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                        <TextField
                            name={`${name}.${i}.v`}
                            label={m.siteSettings_line_n({ n: String(i + 1) })}
                        />
                    </div>
                    <div className="pt-6">
                        <RemoveButton
                            onClick={() => remove(i)}
                            label={m.siteSettings_remove_n({
                                n: String(i + 1),
                            })}
                        />
                    </div>
                </div>
            ))}
            <FieldError name={name} />
            <FieldError name={`${name}.root`} />
            <AddButton
                onClick={() => append({ v: '' })}
                disabled={fields.length >= max}
                label={m.siteSettings_add_line({ max: String(max) })}
            />
        </fieldset>
    )
}

function ListField({
    name,
    field: f,
}: {
    name: string
    field: Extract<SettingField, { t: 'list' }>
}) {
    const { control } = useFormContext()
    const { fields, append, remove } = useFieldArray({ control, name })
    return (
        <fieldset className="space-y-3 rounded-md border p-3">
            <legend className="px-1 text-sm font-medium">{f.label()}</legend>
            {fields.map((row, i) => (
                <div
                    key={row.id}
                    className="space-y-3 rounded-md bg-muted/40 p-3"
                >
                    <div className="flex items-center justify-between">
                        <p className="text-sm font-medium">
                            {f.item({ n: String(i + 1) })}
                        </p>
                        <RemoveButton
                            onClick={() => remove(i)}
                            label={m.siteSettings_remove_n({
                                n: String(i + 1),
                            })}
                        />
                    </div>
                    <SettingFields fields={f.fields} prefix={`${name}.${i}`} />
                </div>
            ))}
            <FieldError name={name} />
            <FieldError name={`${name}.root`} />
            <AddButton
                onClick={() => append(emptyValue(f.fields))}
                disabled={fields.length >= f.max}
                label={m.siteSettings_add_item({ max: String(f.max) })}
            />
        </fieldset>
    )
}
